import * as m from '@staiter/i18n';
import {
  SALES_ORDER_STATUS_LABEL,
  SO_TRANSITION_MATRIX,
  type SalesOrderStatus,
} from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, FolderPlus, Plus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { Button, Dialog } from '~/components/primitives';
import { apiRequest } from '~/lib/api/client';
import {
  createSalesOrderLine,
  deleteSalesOrderLine,
  fetchSalesOrder,
  transitionSalesOrder,
} from '~/lib/api/sales-orders';
import { formatDate, formatMoney, formatQty } from '~/lib/format';
import styles from './_auth.sales-orders.$id.index.module.css';

// FE-90: linear lifecycle. cancelled — terminal side-branch (не часть основного потока).
const SO_LIFECYCLE: readonly SalesOrderStatus[] = [
  'draft',
  'quotation',
  'confirmed',
  'in_progress',
  'delivered',
  'invoiced',
  'closed',
] as const;

/** G3 — Sales Order detail + lines + state machine.
 * EFE-3 Pass 91: rename .tsx → .index.tsx (parent-without-Outlet bug). */
export const Route = createFileRoute('/_auth/sales-orders/$id/')({
  component: SalesOrderDetailPage,
});

function SalesOrderDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['sales-order', id],
    queryFn: () => fetchSalesOrder(id),
  });

  // FE-90 follow-up: Tier 2 transition требует reason (UX designer review CRITICAL —
  // silent transitions = invariant violation). Confirm dialog с reason textarea.
  const [pendingTransition, setPendingTransition] = useState<SalesOrderStatus | null>(null);
  const [transitionReason, setTransitionReason] = useState('');

  const transitionMut = useMutation({
    mutationFn: ({ target, reason }: { target: SalesOrderStatus; reason: string }) =>
      transitionSalesOrder(id, { target_status: target, reason }),
    onSuccess: () => {
      setPendingTransition(null);
      setTransitionReason('');
      qc.invalidateQueries({ queryKey: ['sales-order', id] });
    },
  });

  const so = query.data;

  if (query.isLoading) return <div>...</div>;
  if (!so) return <div>SO not found</div>;

  const allowedTransitions: readonly SalesOrderStatus[] = SO_TRANSITION_MATRIX[so.status] ?? [];

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: m.nav_sales_orders(), to: '/sales-orders' }, { label: so.code }]}
          />
        }
        title={`${so.code} — ${so.counterparty_name ?? so.counterparty_id}`}
        // FE-N Pass 74: formatDate + formatMoney в subtitle (раньше raw ISO + amount with currency code).
        description={`${SALES_ORDER_STATUS_LABEL[so.status]} • ${formatDate(so.order_date)} • ${formatMoney(so.total_amount)}`}
        actions={
          <>
            {/* EFE-3 Pass 91: edit CTA только в draft/quotation. */}
            {(so.status === 'draft' || so.status === 'quotation') && (
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/sales-orders/$id/edit', params: { id: so.id } })}
              >
                {m.common_button_edit()}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() =>
                navigate({ to: '/projects/new', search: { sales_order_id: so.id } as never })
              }
            >
              <FolderPlus size={14} /> {m.so_cta_create_project()}
            </Button>
          </>
        }
      />

      {/* FE-90: StatusStepper — linear lifecycle visible always. cancelled = terminal side-branch. */}
      <section className={styles.stepper} aria-label={m.so_section_state_machine()}>
        <span className={styles.stepperLabel}>{m.so_section_state_machine()}</span>
        {(() => {
          const currentIdx = SO_LIFECYCLE.indexOf(so.status);
          const isCancelled = so.status === 'cancelled';
          return (
            <>
              {SO_LIFECYCLE.map((step, idx) => {
                const cls = isCancelled
                  ? styles.step
                  : idx < currentIdx
                    ? `${styles.step} ${styles.stepDone}`
                    : idx === currentIdx
                      ? `${styles.step} ${styles.stepCurrent}`
                      : styles.step;
                return (
                  <span key={step} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span className={cls}>{SALES_ORDER_STATUS_LABEL[step]}</span>
                    {idx < SO_LIFECYCLE.length - 1 && <span className={styles.arrow}>→</span>}
                  </span>
                );
              })}
              {isCancelled && (
                <span className={`${styles.step} ${styles.stepCancelled}`}>
                  {SALES_ORDER_STATUS_LABEL.cancelled}
                </span>
              )}
            </>
          );
        })()}
        {allowedTransitions.length > 0 && (
          <div className={styles.actions}>
            {allowedTransitions.map((target) => (
              <Button
                key={target}
                size="sm"
                variant={target === 'cancelled' ? 'ghost' : 'secondary'}
                onClick={() => setPendingTransition(target)}
                disabled={transitionMut.isPending}
              >
                <ArrowRight size={12} /> {SALES_ORDER_STATUS_LABEL[target]}
              </Button>
            ))}
          </div>
        )}
      </section>

      {/* FE-90 follow-up: confirm dialog с reason для Tier 2 transition.
          UX designer review CRITICAL — silent transition без reason = audit gap. */}
      <Dialog
        open={pendingTransition !== null}
        onClose={() => {
          setPendingTransition(null);
          setTransitionReason('');
        }}
        title={
          pendingTransition
            ? `${m.so_section_state_machine()} → ${SALES_ORDER_STATUS_LABEL[pendingTransition]}`
            : ''
        }
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <label>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              {m.ui_prichina_perehoda_dlya_audita()}
            </div>
            <textarea
              value={transitionReason}
              onChange={(e) => setTransitionReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: 8,
                background: 'var(--bg-page)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => {
                setPendingTransition(null);
                setTransitionReason('');
              }}
            >
              {m.common_button_cancel()}
            </Button>
            <Button
              onClick={() =>
                pendingTransition &&
                transitionMut.mutate({
                  target: pendingTransition,
                  reason: transitionReason.trim() || 'manual transition',
                })
              }
              loading={transitionMut.isPending}
              disabled={!transitionReason.trim()}
            >
              {m.ui_podtverdit()}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Lines */}
      <SoLinesEditor
        soId={so.id}
        editable={so.status === 'draft' || so.status === 'quotation'}
        currency={so.currency}
        lines={so.lines ?? []}
        onChange={() => qc.invalidateQueries({ queryKey: ['sales-order', id] })}
      />

      {/* G3.7 — Связанные счета */}
      <RelatedInvoicesSection soId={so.id} soStatus={so.status} />
    </div>
  );
}

function RelatedInvoicesSection(props: { soId: string; soStatus?: string }): JSX.Element {
  const nav = useNavigate();
  const query = useQuery({
    queryKey: ['so-related-ar', props.soId],
    queryFn: () =>
      apiRequest(
        `/invoices/ar?sales_order_id=${encodeURIComponent(props.soId)}`,
        z.object({
          items: z.array(
            z
              .object({
                id: z.string().uuid(),
                document_number: z.string(),
                total: z.string().optional(),
              })
              .passthrough(),
          ),
        }),
        { tolerant: true } as never,
      ).catch(() => ({ items: [] })),
  });
  const items = query.data?.items ?? [];
  // FE-92 honest-audit: ghost CTA «+ Счёт» — visible always, disabled с tooltip если status<confirmed.
  const canCreate =
    !props.soStatus || ['confirmed', 'invoiced', 'paid', 'closed'].includes(props.soStatus);
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{m.linkages_section_ar()}</h3>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canCreate}
          title={
            canCreate
              ? 'Создать новый AR-счёт по этому заказу'
              : 'AR-счёт доступен после подтверждения заказа (status ≥ confirmed)'
          }
          onClick={() =>
            nav({ to: '/invoices/new', search: { sales_order_id: props.soId } as never })
          }
        >
          {m.ui_schet()}
        </Button>
      </div>
      {items.length === 0 ? (
        // FE-92: meaningful placeholder + status hint.
        <div
          style={{
            padding: 'var(--space-3)',
            background: 'var(--bg-surface)',
            border: '1px dashed var(--border)',
            borderRadius: 6,
            color: 'var(--text-secondary)',
            fontSize: 13,
          }}
        >
          {m.ui_schetov_poka_net_ar_schet_mozhno_sozdat_posle_podtverzhde()}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((it) => (
            <li
              key={it.id}
              style={{ padding: '4px 0', borderBottom: '1px solid var(--border, #f1f5f9)' }}
            >
              <a href={`/invoices/${it.id}`}>{it.document_number}</a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SoLinesEditor(props: {
  soId: string;
  editable: boolean;
  currency: 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY';
  lines: NonNullable<Awaited<ReturnType<typeof fetchSalesOrder>>['lines']>;
  onChange: () => void;
}): JSX.Element {
  const { soId, editable, currency, lines, onChange } = props;
  const [open, setOpen] = useState(false);
  const itemsQuery = useQuery({
    queryKey: ['items-min', { open }],
    queryFn: () =>
      apiRequest(
        '/items?limit=200',
        z.object({
          items: z.array(z.object({ id: z.string().uuid(), name: z.string() }).passthrough()),
        }),
      ),
    enabled: open,
  });
  const [draft, setDraft] = useState({ item_id: '', qty: '1', uom_code: 'EA', unit_price: '0' });
  const createMut = useMutation({
    mutationFn: () =>
      createSalesOrderLine(soId, {
        item_id: draft.item_id,
        qty: draft.qty,
        uom_code: draft.uom_code,
        unit_price: { amount: draft.unit_price, currency },
      }),
    onSuccess: () => {
      setOpen(false);
      setDraft({ item_id: '', qty: '1', uom_code: 'EA', unit_price: '0' });
      onChange();
    },
  });
  const deleteMut = useMutation({
    mutationFn: (lineId: string) => deleteSalesOrderLine(soId, lineId),
    onSuccess: () => onChange(),
  });

  return (
    <section>
      <h3>{m.so_section_lines()}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>{m.so_col_position()}</th>
            <th>{m.so_col_item()}</th>
            <th>{m.so_col_qty()}</th>
            <th>{m.so_col_uom()}</th>
            <th>{m.so_col_unit_price()}</th>
            <th>{m.so_col_line_total()}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              <td>{l.position}</td>
              <td>{l.item_name ?? l.item_id}</td>
              {/* FE-N Pass 74: formatQty + formatMoney (раньше «2.0000» и «175000.0000 KZT»). */}
              <td>{formatQty(l.qty)}</td>
              <td>{l.uom_code}</td>
              <td>{formatMoney(l.unit_price)}</td>
              <td>{formatMoney(l.line_total)}</td>
              <td>
                {editable && (
                  <Button size="sm" variant="ghost" onClick={() => deleteMut.mutate(l.id)}>
                    ✕
                  </Button>
                )}
              </td>
            </tr>
          ))}
          {lines.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', opacity: 0.6 }}>
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {editable && (
        <>
          <Button size="sm" onClick={() => setOpen(true)} style={{ marginTop: 8 }}>
            <Plus size={12} /> {m.so_section_lines()}
          </Button>
          <Dialog open={open} onClose={() => setOpen(false)} title={m.so_section_lines()}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMut.mutate();
              }}
              style={{ display: 'grid', gap: 8 }}
            >
              <label>
                {m.so_col_item()}
                <select
                  value={draft.item_id}
                  onChange={(e) => setDraft({ ...draft, item_id: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {(itemsQuery.data?.items ?? []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {m.so_col_qty()}
                <input
                  value={draft.qty}
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                />
              </label>
              <label>
                {m.so_col_uom()}
                <input
                  value={draft.uom_code}
                  onChange={(e) => setDraft({ ...draft, uom_code: e.target.value })}
                />
              </label>
              <label>
                {m.so_col_unit_price()}
                <input
                  value={draft.unit_price}
                  onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                />
              </label>
              <Button type="submit" disabled={createMut.isPending || !draft.item_id}>
                {m.so_section_lines()}
              </Button>
            </form>
          </Dialog>
        </>
      )}
    </section>
  );
}
